import { type IChatMessage, EMessageType } from '@kognitos/lattice';

import { createStore } from './config';

const REPLACEABLE_MESSAGE_ID_PREFIXES = ['todo-progress:'];
const INTERNAL_TOOL_COUNT_REGEX = /, \d+ files$/;

/**
 * Returns whether a duplicate incoming message should replace the stored one.
 *
 * @param messageId - Chat message id
 * @returns Whether duplicate messages with this id should be replaced
 */
function isReplaceableMessageId(messageId: string): boolean {
  return REPLACEABLE_MESSAGE_ID_PREFIXES.some(prefix =>
    messageId.startsWith(prefix),
  );
}

/**
 * Returns whether an incoming duplicate represents an aggregate tool-call row.
 *
 * @param message - Chat message
 * @returns Whether duplicate messages with this content should replace the prior row
 */
function isReplaceableAggregateToolMessage(message: IChatMessage): boolean {
  return (
    typeof message.content === 'string' &&
    INTERNAL_TOOL_COUNT_REGEX.test(message.content)
  );
}

/**
 * Connection update info for add_connection messages
 */
interface IConnectionUpdate {
  connectionId: string;
  bookName: string;
  bookVersion: string;
}

/**
 * State for a single automation's chat
 */
interface IAutomationChatState {
  messages: IChatMessage[];
  processedIds: Set<string>;
  processedPages: Set<number>;
  addConnectionQueue: Map<string, IConnectionUpdate>;
  threadId: string | undefined;
}

/**
 * Quill Chat Store State
 * Manages chat messages state per automation to persist across component remounts
 */
export interface IQuillChatState {
  // State keyed by automationId
  chatsByAutomation: Record<string, IAutomationChatState>;

  // Actions
  /**
   * Gets messages for an automation
   */
  getMessages: (automationId: string) => IChatMessage[];

  /**
   * Sets all messages for an automation
   */
  setMessages: (automationId: string, messages: IChatMessage[]) => void;

  /**
   * Updates messages using a callback function (like React setState)
   */
  updateMessages: (
    automationId: string,
    updater: (prev: IChatMessage[]) => IChatMessage[],
  ) => void;

  /**
   * Adds a message if not already processed.
   * Returns true if message was added, false if already processed.
   */
  addMessage: (automationId: string, message: IChatMessage) => boolean;

  /**
   * Adds multiple messages in a single state update, skipping duplicates.
   * More efficient than calling addMessage in a loop during streaming flushes.
   */
  addMessages: (automationId: string, messages: IChatMessage[]) => void;

  /**
   * Checks if a message ID has been processed
   */
  isProcessed: (automationId: string, messageId: string) => boolean;

  /**
   * Marks a message ID as processed
   */
  markProcessed: (automationId: string, messageId: string) => void;

  /**
   * Checks if a page has been processed
   */
  isPageProcessed: (automationId: string, pageIndex: number) => boolean;

  /**
   * Marks a page as processed
   */
  markPageProcessed: (automationId: string, pageIndex: number) => void;

  /**
   * Clears processed pages (used when threadId changes)
   */
  clearProcessedPages: (automationId: string) => void;

  /**
   * Gets the add connection queue for an automation
   */
  getAddConnectionQueue: (
    automationId: string,
  ) => Map<string, IConnectionUpdate>;

  /**
   * Adds an entry to the add connection queue
   */
  addToConnectionQueue: (
    automationId: string,
    targetMessageId: string,
    connectionUpdate: IConnectionUpdate,
  ) => void;

  /**
   * Removes an entry from the add connection queue
   */
  removeFromConnectionQueue: (
    automationId: string,
    targetMessageId: string,
  ) => void;

  /**
   * Clears the add connection queue
   */
  clearConnectionQueue: (automationId: string) => void;

  /**
   * Gets the thread ID for an automation
   */
  getThreadId: (automationId: string) => string | undefined;

  /**
   * Sets the thread ID for an automation
   */
  setThreadId: (automationId: string, threadId: string | undefined) => void;

  /**
   * Clears all state for an automation
   */
  clearAutomation: (automationId: string) => void;
}

/**
 * Creates an empty chat state for an automation
 */
const createEmptyAutomationState = (): IAutomationChatState => ({
  messages: [],
  processedIds: new Set(),
  processedPages: new Set(),
  addConnectionQueue: new Map(),
  threadId: undefined,
});

/**
 * Gets or creates the chat state for an automation
 */
const getAutomationState = (
  chatsByAutomation: Record<string, IAutomationChatState>,
  automationId: string,
): IAutomationChatState => {
  return chatsByAutomation[automationId] || createEmptyAutomationState();
};

export const useQuillChatStore = createStore<IQuillChatState>(
  'quill-chat-store',
  (_, get, api) => ({
    chatsByAutomation: {},

    getMessages: (automationId: string) => {
      const state = get().chatsByAutomation[automationId];
      return state?.messages || [];
    },

    setMessages: (automationId: string, messages: IChatMessage[]) => {
      const currentChats = get().chatsByAutomation;
      const currentState = getAutomationState(currentChats, automationId);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              messages,
              processedIds:
                messages.length === 0 ? new Set() : currentState.processedIds,
            },
          },
        },
        false,
        { type: 'quillChat/setMessages', automationId },
      );
    },

    updateMessages: (
      automationId: string,
      updater: (prev: IChatMessage[]) => IChatMessage[],
    ) => {
      const currentChats = get().chatsByAutomation;
      const currentState = getAutomationState(currentChats, automationId);
      const newMessages = updater(currentState.messages);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              messages: newMessages,
            },
          },
        },
        false,
        { type: 'quillChat/updateMessages', automationId },
      );
    },

    addMessage: (automationId: string, message: IChatMessage) => {
      const messageId = message.id;
      if (!messageId) {
        return false;
      }

      let wasAdded = false;

      // Combine check and update in single setState for atomicity
      api.setState(
        state => {
          const currentState = getAutomationState(
            state.chatsByAutomation,
            automationId,
          );

          // Check if already processed - if so, return unchanged state
          if (currentState.processedIds.has(messageId)) {
            return state;
          }

          wasAdded = true;
          const newProcessedIds = new Set(currentState.processedIds);
          newProcessedIds.add(messageId);

          // Check if the last message is an empty thinking message and remove it
          let existingMessages = [...currentState.messages];
          const lastMessage = existingMessages[existingMessages.length - 1];
          if (
            lastMessage &&
            lastMessage.type === EMessageType.THINKING &&
            lastMessage.content === ''
          ) {
            existingMessages = existingMessages.slice(0, -1);
          }

          return {
            chatsByAutomation: {
              ...state.chatsByAutomation,
              [automationId]: {
                ...currentState,
                messages: [...existingMessages, message],
                processedIds: newProcessedIds,
              },
            },
          };
        },
        false,
        { type: 'quillChat/addMessage', automationId, messageId },
      );

      return wasAdded;
    },

    addMessages: (automationId: string, msgs: IChatMessage[]) => {
      if (msgs.length === 0) return;

      api.setState(
        state => {
          const currentState = getAutomationState(
            state.chatsByAutomation,
            automationId,
          );

          let existingMessages = currentState.messages;
          const lastMessage = existingMessages[existingMessages.length - 1];
          if (
            lastMessage &&
            lastMessage.type === EMessageType.THINKING &&
            lastMessage.content === ''
          ) {
            existingMessages = existingMessages.slice(0, -1);
          }

          const newProcessedIds = new Set(currentState.processedIds);
          const nextMessages = [...existingMessages];
          let didChange = false;

          for (const msg of msgs) {
            const messageId = msg.id;
            if (!messageId) continue;

            const existingIndex = nextMessages.findIndex(
              message => message.id === messageId,
            );

            if (newProcessedIds.has(messageId)) {
              if (
                (isReplaceableMessageId(messageId) ||
                  isReplaceableAggregateToolMessage(msg)) &&
                existingIndex >= 0
              ) {
                nextMessages[existingIndex] = msg;
                didChange = true;
              }
              continue;
            }

            newProcessedIds.add(messageId);
            nextMessages.push(msg);
            didChange = true;
          }

          if (!didChange) return state;

          return {
            chatsByAutomation: {
              ...state.chatsByAutomation,
              [automationId]: {
                ...currentState,
                messages: nextMessages,
                processedIds: newProcessedIds,
              },
            },
          };
        },
        false,
        {
          type: 'quillChat/addMessages',
          automationId,
          count: msgs.length,
        },
      );
    },

    isProcessed: (automationId: string, messageId: string) => {
      const state = get().chatsByAutomation[automationId];
      return state?.processedIds.has(messageId) ?? false;
    },

    markProcessed: (automationId: string, messageId: string) => {
      const currentChats = get().chatsByAutomation;
      const currentState = getAutomationState(currentChats, automationId);

      if (currentState.processedIds.has(messageId)) {
        return;
      }

      const newProcessedIds = new Set(currentState.processedIds);
      newProcessedIds.add(messageId);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              processedIds: newProcessedIds,
            },
          },
        },
        false,
        { type: 'quillChat/markProcessed', automationId, messageId },
      );
    },

    isPageProcessed: (automationId: string, pageIndex: number) => {
      const state = get().chatsByAutomation[automationId];
      return state?.processedPages.has(pageIndex) ?? false;
    },

    markPageProcessed: (automationId: string, pageIndex: number) => {
      const currentChats = get().chatsByAutomation;
      const currentState = getAutomationState(currentChats, automationId);

      if (currentState.processedPages.has(pageIndex)) {
        return;
      }

      const newProcessedPages = new Set(currentState.processedPages);
      newProcessedPages.add(pageIndex);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              processedPages: newProcessedPages,
            },
          },
        },
        false,
        { type: 'quillChat/markPageProcessed', automationId, pageIndex },
      );
    },

    clearProcessedPages: (automationId: string) => {
      const currentChats = get().chatsByAutomation;
      const currentState = currentChats[automationId];

      if (!currentState) {
        return;
      }

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              processedPages: new Set(),
              addConnectionQueue: new Map(),
            },
          },
        },
        false,
        { type: 'quillChat/clearProcessedPages', automationId },
      );
    },

    getAddConnectionQueue: (automationId: string) => {
      const state = get().chatsByAutomation[automationId];
      return state?.addConnectionQueue || new Map();
    },

    addToConnectionQueue: (
      automationId: string,
      targetMessageId: string,
      connectionUpdate: IConnectionUpdate,
    ) => {
      const currentChats = get().chatsByAutomation;
      const currentState = getAutomationState(currentChats, automationId);

      const newQueue = new Map(currentState.addConnectionQueue);
      newQueue.set(targetMessageId, connectionUpdate);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              addConnectionQueue: newQueue,
            },
          },
        },
        false,
        {
          type: 'quillChat/addToConnectionQueue',
          automationId,
          targetMessageId,
        },
      );
    },

    removeFromConnectionQueue: (
      automationId: string,
      targetMessageId: string,
    ) => {
      const currentChats = get().chatsByAutomation;
      const currentState = currentChats[automationId];

      if (!currentState) {
        return;
      }

      const newQueue = new Map(currentState.addConnectionQueue);
      newQueue.delete(targetMessageId);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              addConnectionQueue: newQueue,
            },
          },
        },
        false,
        {
          type: 'quillChat/removeFromConnectionQueue',
          automationId,
          targetMessageId,
        },
      );
    },

    clearConnectionQueue: (automationId: string) => {
      const currentChats = get().chatsByAutomation;
      const currentState = currentChats[automationId];

      if (!currentState) {
        return;
      }

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              addConnectionQueue: new Map(),
            },
          },
        },
        false,
        { type: 'quillChat/clearConnectionQueue', automationId },
      );
    },

    getThreadId: (automationId: string) => {
      const state = get().chatsByAutomation[automationId];
      return state?.threadId;
    },

    setThreadId: (automationId: string, threadId: string | undefined) => {
      const currentChats = get().chatsByAutomation;
      const currentState = getAutomationState(currentChats, automationId);

      api.setState(
        {
          chatsByAutomation: {
            ...currentChats,
            [automationId]: {
              ...currentState,
              threadId,
            },
          },
        },
        false,
        { type: 'quillChat/setThreadId', automationId, threadId },
      );
    },

    clearAutomation: (automationId: string) => {
      const currentChats = get().chatsByAutomation;
      const { [automationId]: _, ...restChats } = currentChats;

      api.setState(
        {
          chatsByAutomation: restChats,
        },
        false,
        { type: 'quillChat/clearAutomation', automationId },
      );
    },
  }),
);

// Selectors
export const selectChatsByAutomation = (state: IQuillChatState) =>
  state.chatsByAutomation;
export const selectGetMessages = (state: IQuillChatState) => state.getMessages;
export const selectSetMessages = (state: IQuillChatState) => state.setMessages;
export const selectUpdateMessages = (state: IQuillChatState) =>
  state.updateMessages;
export const selectAddMessage = (state: IQuillChatState) => state.addMessage;
export const selectAddMessages = (state: IQuillChatState) => state.addMessages;
export const selectIsProcessed = (state: IQuillChatState) => state.isProcessed;
export const selectMarkProcessed = (state: IQuillChatState) =>
  state.markProcessed;
export const selectIsPageProcessed = (state: IQuillChatState) =>
  state.isPageProcessed;
export const selectMarkPageProcessed = (state: IQuillChatState) =>
  state.markPageProcessed;
export const selectClearProcessedPages = (state: IQuillChatState) =>
  state.clearProcessedPages;
export const selectGetAddConnectionQueue = (state: IQuillChatState) =>
  state.getAddConnectionQueue;
export const selectAddToConnectionQueue = (state: IQuillChatState) =>
  state.addToConnectionQueue;
export const selectRemoveFromConnectionQueue = (state: IQuillChatState) =>
  state.removeFromConnectionQueue;
export const selectClearConnectionQueue = (state: IQuillChatState) =>
  state.clearConnectionQueue;
export const selectGetThreadId = (state: IQuillChatState) => state.getThreadId;
export const selectSetThreadId = (state: IQuillChatState) => state.setThreadId;
export const selectClearAutomation = (state: IQuillChatState) =>
  state.clearAutomation;

/**
 * Selector factory to get messages for a specific automation
 * Usage: useQuillChatStore(selectMessagesForAutomation(automationId))
 */
export const selectMessagesForAutomation =
  (automationId: string) => (state: IQuillChatState) =>
    state.chatsByAutomation[automationId]?.messages || [];
